import { isEqual } from 'date-fns';
import isEmpty from 'lodash-es/isEmpty';

import { ReservationFlight, ReservationHotel } from '@user-history/entities/history-booking-detail';
import { AirValidationResponse } from '@app/entities/air-validation.response';
import { environment as ENV } from '@environment';
import { CheckboxFilter } from '@shared/filters/entities/fetch-filters.response';
import { RefundableTypes } from '@ux/entities/refundable-types';
import { JETSMART_AIR_CODE_1, JETSMART_AIR_CODE_2, JETSMART_AIR_CODE_3 } from '@shared/luggage/constants/airline-code.constants';
import { isConfirmReservationByStatus } from '@ux/mappers/reservation-status.mapper';
import { Countries } from '@ux/entities/country.enum';
import { getCountryCode } from '@ux/mappers/place.mapper';
import { isInternationalTripByCode } from '@app/mappers/is-international-trip.mapper';

const locale = 'en-US';
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export enum ProvidersNotExcludedFromCancellation {
  Amadeus = 'Amadeus'
}

export enum ProvidersExcludedFromCancellation {
  Wingo = 'Wingo',
  VivaAerobus = 'VivaAerobus',
  JetSmart = 'JetSmart'
}

export function isNotDisabledByAirlinesInCancellationByHistory(reservation: ReservationFlight): boolean {
  return !!reservation?.Sectors && !!isNotDisabledByAirlinesInCancellationByReservation(reservation);
}

export function flightFreeCancellationBookingEnabled(
  airValidation: AirValidationResponse,
  provider: string
): boolean {
  const sectors = airValidation?.TripOption?.Sectors;
  if (!sectors?.length) return false;

  const isOneWay = sectors.length === 1;

  const flights = sectors[0]?.Flights;
  if (!flights?.length) return false;

  const firstFlight = flights[0];
  const lastFlight = flights[flights.length - 1];

  const countryCodeDeparture = getCountryCode(firstFlight?.DepartureAirport);
  const countryCodeArrival = getCountryCode(lastFlight?.ArrivalAirport);

  const isInternationalTrip = 
    isInternationalTripByCode(countryCodeDeparture) || 
    isInternationalTripByCode(countryCodeArrival);

  const isCancellationEnabled =
    !!ENV.countryModules?.cancellation?.flight_enabled &&
    isNotDisabledByAirlinesInCancellationByBooking(airValidation, provider);

  if (ENV.countryInfo.iso_code !== Countries.Colombia) {
    return isCancellationEnabled;
  }
  return isCancellationEnabled && !(isOneWay && isInternationalTrip);
}

export function getEnabledCancellationHotelsByEtas(reservations: ReservationHotel[]): boolean {
  const isValidCancellation = !!ENV.countryModules?.cancellation?.hotel_enabled &&
    reservations.every(reservation => {
      return reservation?.Rooms[0]?.RefundableType === RefundableTypes.REFUNDABLE;
    });
  return compareDatesByHotelCancellationWithDeviceTimezone(
    reservations?.[0].Checkin, isValidCancellation);
}

export function getEnabledCancellationFlightsByEtas(reservations: ReservationFlight[]): boolean {
  const isValidCancellation = !!ENV.countryModules?.cancellation?.flight_enabled &&
    reservations.every(reservation => {
      return isNotDisabledByAirlinesInCancellationByReservation(reservation) &&
        isConfirmReservationByStatus(reservation.Status) &&
        isEmpty(reservation.History);
    });
  return compareDatesByCancellationWithDeviceTimezone(
    reservations?.[0]?.Sectors?.[0]?.Flights?.[0]?.DepartureDate, isValidCancellation);
}

export function isNotDisabledByAirlinesInCancellationByResult(airlines: CheckboxFilter[]): boolean {
  return !!airlines && airlines?.every(airline => isNotDisabledByProviderOnCancellation(airline?.Text));
}

export function disabledAirlinesInCancellation(airlines: CheckboxFilter[]): string[] {
  return airlines ? airlines?.filter(airline =>
    !isNotDisabledByProviderOnCancellation(airline?.Text)).map(airline => airline.Text) : [];
}

function isNotDisabledByAirlinesInCancellationByBooking(airValidation: AirValidationResponse, provider: string): boolean {
  return !!airValidation?.TripOption?.Sectors && airValidation?.TripOption?.Sectors.every(
    sector => !!sector?.Flights && sector?.Flights.every(flight => isNotDisabledByProviderOnCancellation(
      flight.AirlineCode, provider as ProvidersExcludedFromCancellation
    )));
}

function isNotDisabledByAirlinesInCancellationByReservation(reservation: ReservationFlight): boolean {
  return !!reservation?.Sectors && reservation?.Sectors.every(sector =>
    !!sector?.Flights && sector?.Flights.every(flight => isNotDisabledByProviderOnCancellation(flight.AirlineCode,
      reservation.BookingSource as ProvidersNotExcludedFromCancellation)));
}

function isNotDisabledByProviderOnCancellation(
  airlineCode: string, source: ProvidersNotExcludedFromCancellation | ProvidersExcludedFromCancellation = null
): boolean {
  const disabledAirlines = ENV.countryModules?.cancellation?.disabled_by_airlines || [];
  const isNotDisabled = !disabledAirlines.includes(airlineCode);

  const isWingoProvider = source === ProvidersExcludedFromCancellation.Wingo;
  const isVivaAerobus = source === ProvidersExcludedFromCancellation.VivaAerobus;
  const isJetSmart = source === ProvidersExcludedFromCancellation.JetSmart;

  if (isJetSmart || isWingoProvider || isVivaAerobus) {
    return false;
  }

  switch (airlineCode) {
    case JETSMART_AIR_CODE_1:
    case JETSMART_AIR_CODE_2:
    case JETSMART_AIR_CODE_3:
      return isNotDisabled || source === ProvidersNotExcludedFromCancellation.Amadeus;
    default:
      return isNotDisabled;
  }
}

function compareDatesByHotelCancellationWithDeviceTimezone(
  date: string, isValidCancellation: boolean
): boolean {
  const now = new Date(new Date().toLocaleString(locale, { timeZone }));
  const departureDate = new Date(new Date(date).toLocaleString(locale, { timeZone }));
  if (!isEqualDates(date) && now > departureDate) {
    return !!isValidCancellation && (now.getHours() <= departureDate.getHours()) &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((Math.abs((now as any) - (departureDate as any)) / 36e5) > 2);
  } return !!isValidCancellation;
}

function compareDatesByCancellationWithDeviceTimezone(
  date: string, isValidCancellation: boolean
): boolean {
  const now = new Date(new Date().toLocaleString(locale, { timeZone }));
  const departureDate = new Date(new Date(date).toLocaleString(locale, { timeZone }));
  if (isEqualDates(date)) {
    return !!isValidCancellation && (now.getHours() <= departureDate.getHours()) &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((Math.abs((now as any) - (departureDate as any)) / 36e5) > 2);
  } return !!isValidCancellation;
}

function isEqualDates(date: string): boolean {
  const now = new Date(new Date().toLocaleString(locale, { timeZone }));
  const departureDate = new Date(new Date(date).toLocaleString(locale, { timeZone }));
  now.setHours(0, 0, 0, 0);
  departureDate.setHours(0, 0, 0, 0);
  return isEqual(now, departureDate);
}

